import type { QueryList, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import moment from 'moment-timezone';

import { TimeService } from '@recruitee/common';
import { I18nService } from '@recruitee/i18n';

import { DropdownItemComponent } from '../../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent, DropdownWidth } from '../../../common/dropdown/dropdown.component';

@Component({
  selector: 'rt-time-picker-dropdown',
  templateUrl: './time-picker-dropdown.component.html',
  styleUrls: ['./time-picker-dropdown.component.less'],
  host: {
    '(window:keydown)': 'onKeyDown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropdownComponent, DropdownItemComponent],
})
export class TimePickerDropdownComponent {
  @Input() public currentSelected: string;
  @Input() public duration: boolean = false;
  @Input() public blockPastTime: boolean = false;
  @Input() public durationInterval: number = 5;
  @Input() public durationMin: number = 0;
  @Input() public durationMax: number = 300;
  @Input() public timeInterval: number = 0;

  @Output() public timeSelect: EventEmitter<string> = new EventEmitter();

  @ViewChild('timeDropdownContainer', { read: ElementRef, static: true })
  public timeDropdownContainerRef: ElementRef;
  @ViewChildren('timeItemsQuery', { read: ElementRef })
  public itemsQueryList: QueryList<ElementRef>;
  public DropdownWidth = DropdownWidth;
  public timesArr: string[] = [];
  public selectionIndex: number | null = null;

  private currentItem: HTMLElement;

  constructor(
    private i18n: I18nService,
    private timeService: TimeService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    if (this.duration) {
      this.prepareDurationData();
    } else {
      this.prepareTimeArray();
    }
    this.setSelectionIndex();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.blockPastTime &&
      changes.blockPastTime.currentValue !== changes.blockPastTime &&
      changes.blockPastTime.previousValue
    ) {
      this.prepareTimeArray();
    }
  }

  public ngAfterViewInit(): void {
    this.setScrollOffset();
  }

  public trackBy(index: number, item: string): string {
    return item;
  }

  public onTimeSelect(time: string): void {
    this.timeSelect.emit(time);
  }

  public onKeyDown($event: KeyboardEvent): void {
    let keyCaptured = true;
    switch ($event.key) {
      case 'Down':
      case 'ArrowDown': {
        const index = this.selectionIndex;
        if (index && index < this.timesArr.length - 1) {
          this.selectionIndex = index + 1;
        }
        break;
      }
      case 'Up':
      case 'ArrowUp': {
        const index = this.selectionIndex;
        if (index && index > 0) {
          this.selectionIndex = index - 1;
        }
        break;
      }
      case 'Enter': {
        this.currentItem.click();
        break;
      }
      default: {
        keyCaptured = false;
      }
    }
    if (keyCaptured) {
      this.setScrollOffset();
      $event.preventDefault();
    }
    this.cdRef.detectChanges();
  }

  private setScrollOffset(): void {
    const timeContainer = this.timeDropdownContainerRef.nativeElement;
    const timeItemRef = this.itemsQueryList.find((item, index) => index === this.selectionIndex);

    if (!timeContainer || !timeItemRef) {
      return;
    }

    const timeItem = timeItemRef.nativeElement;
    this.currentItem = timeItem;

    const itemRelativeTop = timeItem.offsetTop - timeContainer.offsetTop;
    timeContainer.scrollTop = itemRelativeTop;
  }

  private prepareDurationData(): void {
    const data: string[] = [];
    const mins = ' ' + this.i18n.translate('rtu.interview.schedule_dialog.minutes');

    for (let i = this.durationMin; i <= this.durationMax; i += this.durationInterval) {
      data.push(i.toString() + mins);
    }

    this.timesArr = data;
  }

  private prepareTimeArray(): void {
    const minutesSteps = parseInt(`${60 / this.timeInterval}`, 10) || 1;
    const { startingHour, startingMinutes } = this.getStartingSteps();
    const timesArr: string[] = [];

    for (let hours = startingHour; hours <= 23; hours++) {
      let minutes = hours === startingHour ? startingMinutes : 0;

      for (minutes; minutes <= minutesSteps - 1; minutes++) {
        const time = moment(`${hours}:${minutes * this.timeInterval}`, 'H:mm').format('LT');
        timesArr.push(time);
      }
    }

    this.timesArr = timesArr;
  }

  private getStartingSteps() {
    return {
      startingHour: this.blockPastTime ? this.timeService.getCurrentHour() : 0,
      startingMinutes: this.blockPastTime ? this.getStartingMinutesStep() : 0,
    };
  }

  private getStartingMinutesStep() {
    return Math.floor(this.timeService.getCurrentMinutes() / this.timeInterval) + 1;
  }

  private getSelectionIndex(): number {
    if (!this.currentSelected && !this.duration) {
      const currentHour = this.timeService.getCurrentHour();
      return currentHour * 4;
    } else if (!this.currentSelected && this.duration) {
      return 0;
    } else if (this.duration) {
      const currentSelectedTime = parseInt(this.currentSelected, 10);
      return (
        this.timesArr.reduce(
          (result, time, index) => (parseInt(time, 10) <= currentSelectedTime ? index : result),
          <number | null>null,
        ) || 0
      );
    } else {
      return this.timesArr.findIndex(time => {
        const currentTime = moment(this.currentSelected, 'LT');
        const timeToSelect = moment(time, 'LT');

        return currentTime.isSameOrBefore(timeToSelect);
      });
    }
  }

  private setSelectionIndex(): void {
    const index = this.getSelectionIndex();
    this.selectionIndex = index;
  }
}
